import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useEffect, useMemo, useRef } from "react";
import {
    CreateServiceAccountRequest,
    DeleteServiceAccountRequest,
    GetServiceAccountRequest,
    ListServiceAccountsRequest,
    UpdateServiceAccountRequest,
} from "gitpod-next-api/gitpod/v1/service_account_pb";
import type { ServiceAccount } from "gitpod-next-api/gitpod/v1/service_account_pb";
import { Timestamp } from "@bufbuild/protobuf";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { useEventInterest } from "@/hooks/use-event-interest";
import { type GitpodAPI } from "@/api";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";

export type PlainServiceAccount = PlainMessage<ServiceAccount>;

const toPlainServiceAccount = (serviceAccount: ServiceAccount): PlainServiceAccount => {
    return toPlainMessage(serviceAccount);
};

export const serviceAccountsQueryKey = {
    list: (search?: string) => keyWithPrincipal(["service-accounts", "list", { search: search || "" }]),
    listByIds: (serviceAccountIds: string[]) => keyWithPrincipal(["service-accounts", "list", "byIds", { serviceAccountIds }]),
    cursorPage: (search?: string, token?: string) =>
        keyWithPrincipal(["service-accounts", "cursorPage", { search: search || "", token: token || "" }]),
    get: (serviceAccountId: string) => keyWithPrincipal(["service-accounts", "get", serviceAccountId]),
    serviceAccount: (serviceAccountId: string) => keyWithPrincipal(["serviceAccount", serviceAccountId]),
    accessToken: (serviceAccountId: string) => keyWithPrincipal(["service-accounts", "accessToken", serviceAccountId]),
};

const invalidateCursorPages = (client: QueryClient) =>
    client.invalidateQueries({ queryKey: serviceAccountsQueryKey.cursorPage().slice(0, -1) });

export const handleServiceAccountEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.operation) {
        case ResourceOperation.CREATE: {
            await client.invalidateQueries({ queryKey: serviceAccountsQueryKey.list().slice(0, -1) });
            await invalidateCursorPages(client);
            break;
        }
        case ResourceOperation.UPDATE:
        case ResourceOperation.UPDATE_STATUS: {
            const serviceAccount = await refetchServiceAccount(api, evt.resourceId);
            setServiceAccountInCache(
                client,
                evt.resourceId,
                serviceAccount ? toPlainServiceAccount(serviceAccount) : undefined,
            );
            await invalidateCursorPages(client);
            break;
        }
        case ResourceOperation.DELETE: {
            setServiceAccountInCache(client, evt.resourceId, undefined);
            await invalidateCursorPages(client);
            break;
        }
    }
};

const refetchServiceAccount = async (api: GitpodAPI, serviceAccountId: string) => {
    try {
        const response = await api.serviceAccountService.getServiceAccount({ serviceAccountId });
        return response.serviceAccount;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch service account", serviceAccountId);
        }
    }
};

const setServiceAccountInCache = (
    client: QueryClient,
    serviceAccountId: string,
    serviceAccount: PlainServiceAccount | undefined,
) => {
    client.setQueryData(serviceAccountsQueryKey.get(serviceAccountId), serviceAccount ? serviceAccount : null);

    // Update all list query caches (regardless of search filter) via prefix match
    const listQueries = client.getQueriesData<{ serviceAccounts: PlainServiceAccount[] }>({
        queryKey: serviceAccountsQueryKey.list().slice(0, -1),
    });
    for (const [queryKey, currentData] of listQueries) {
        if (!currentData) {
            continue;
        }

        const updatedList = currentData.serviceAccounts
            .map((sa: PlainServiceAccount) => {
                if (sa.id !== serviceAccountId) {
                    return sa;
                }
                return serviceAccount;
            })
            .filter((sa) => !!sa);

        // Add SA to the top of the list only for the unfiltered query (when it's not already there)
        const isUnfilteredList = JSON.stringify(queryKey) === JSON.stringify(serviceAccountsQueryKey.list());
        if (serviceAccount && isUnfilteredList && !currentData.serviceAccounts.some((sa) => sa.id === serviceAccountId)) {
            updatedList.unshift(serviceAccount);
        }

        client.setQueryData(queryKey, { serviceAccounts: updatedList });
    }
};

export const useListServiceAccounts = ({ enabled = true, search = "" }: { enabled?: boolean; search?: string } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    useEventInterest({ resourceType: ResourceType.SERVICE_ACCOUNT });

    return useQuery({
        queryKey: serviceAccountsQueryKey.list(search),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const serviceAccounts = await autoPaginate(
                (params) => api.serviceAccountService.listServiceAccounts(params),
                search ? { filter: { search } } : {},
                (response) => response.serviceAccounts,
            );

            return {
                serviceAccounts: serviceAccounts.map(toPlainServiceAccount),
            };
        },
        enabled: !!user && enabled,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
        placeholderData: keepPreviousData,
    });
};

type ServiceAccountsCursorPageData = {
    serviceAccounts: PlainServiceAccount[];
    nextToken: string;
};

/**
 * Fetches a single page of service accounts using an opaque cursor token.
 * Manages cursor state internally and resets when filters change.
 */
export const useServiceAccountsCursorPage = ({ search = "", enabled = true }: { search?: string; enabled?: boolean } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const prevSearchRef = useRef(search);
    useEffect(() => {
        if (prevSearchRef.current !== search) {
            prevSearchRef.current = search;
            cursor.reset();
        }
    }, [search, cursor]);

    const query = useQuery({
        queryKey: serviceAccountsQueryKey.cursorPage(search, cursor.currentToken),
        queryFn: async (): Promise<ServiceAccountsCursorPageData> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.serviceAccountService.listServiceAccounts({
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token: cursor.currentToken }),
                filter: search ? { search } : undefined,
            });

            return {
                serviceAccounts: response.serviceAccounts.map(toPlainServiceAccount),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        enabled: !!user && enabled,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
        placeholderData: keepPreviousData,
    });

    return { ...query, cursor };
};

const SERVICE_ACCOUNT_BATCH_SIZE = 25;

/**
 * Get a service account from the query cache by ID.
 * Returns undefined if not in cache.
 */
export const getServiceAccountFromCache = (queryClient: QueryClient, serviceAccountId: string): PlainServiceAccount | undefined => {
    return queryClient.getQueryData<PlainServiceAccount>(serviceAccountsQueryKey.serviceAccount(serviceAccountId));
};

/**
 * Hook that prefetches service accounts by their IDs and populates individual caches.
 * 
 * This hook:
 * 1. Checks which IDs are missing from the cache
 * 2. Batches missing IDs into groups of 25
 * 3. Fetches each batch and populates individual ['serviceAccount', id] caches
 * 
 * Components should read service account data using getServiceAccountFromCache() or useQueryClient().
 */
export const usePrefetchServiceAccountsByIds = (serviceAccountIds: string[], { enabled = true }: { enabled?: boolean } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const queryClient = useQueryClient();

    // Find IDs not already in cache
    const missingIds = useMemo(() => {
        return serviceAccountIds.filter((id) => !queryClient.getQueryData(serviceAccountsQueryKey.serviceAccount(id)));
    }, [serviceAccountIds, queryClient]);

    // Batch missing IDs into groups of 25 (API filter limitation)
    const batches = useMemo(() => {
        const result: string[][] = [];
        for (let i = 0; i < missingIds.length; i += SERVICE_ACCOUNT_BATCH_SIZE) {
            result.push(missingIds.slice(i, i + SERVICE_ACCOUNT_BATCH_SIZE));
        }

        return result;
    }, [missingIds]);

    return useQueries({
        queries: batches.map((batch) => ({
            queryKey: serviceAccountsQueryKey.listByIds(batch),
            queryFn: async () => {
                if (!user) {
                    throw new Error("User not authenticated");
                }
                const request = new ListServiceAccountsRequest({
                    filter: { serviceAccountIds: batch },
                });
                const response = await api.serviceAccountService.listServiceAccounts(request);
                const serviceAccounts = response.serviceAccounts.map(toPlainServiceAccount);
                
                // Populate individual service account caches
                for (const sa of serviceAccounts) {
                    queryClient.setQueryData(serviceAccountsQueryKey.serviceAccount(sa.id), sa);
                }
                
                return serviceAccounts;
            },
            enabled: enabled && !!user && batch.length > 0,
            staleTime: Infinity, // Batch query doesn't need to refetch
            gcTime: 0, // Discard batch result immediately, we only care about side effects
            retry: defaultRetry,
        })),
        combine: (results) => ({
            isLoading: results.some((r) => r.isLoading),
            isPending: results.some((r) => r.isPending),
            isError: results.some((r) => r.isError),
        }),
    });
};

export type useGetServiceAccountParams = {
    serviceAccountId: string;
    enabled?: boolean;
};

export const useGetServiceAccount = ({ serviceAccountId, enabled = true }: useGetServiceAccountParams) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: serviceAccountsQueryKey.get(serviceAccountId),
        queryFn: async () => {
            const request = new GetServiceAccountRequest({ serviceAccountId });
            const { serviceAccount } = await api.serviceAccountService.getServiceAccount(request);

            if (!serviceAccount) {
                throw new Error("Error fetching service account");
            }

            return toPlainServiceAccount(serviceAccount);
        },
        enabled: enabled && !!serviceAccountId,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
    });
};

export type CreateServiceAccountParams = {
    name: string;
    description?: string;
    validUntil: Date;
};

export const useCreateServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name, description, validUntil }: CreateServiceAccountParams) => {
            const request = new CreateServiceAccountRequest({
                name,
                description,
                validUntil: Timestamp.fromDate(validUntil),
            });
            const { serviceAccount } = await api.serviceAccountService.createServiceAccount(request);

            if (!serviceAccount) {
                throw new Error("Error fetching service account");
            }

            return toPlainServiceAccount(serviceAccount);
        },
        onSuccess: (newServiceAccount) => {
            setServiceAccountInCache(queryClient, newServiceAccount.id, newServiceAccount);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateServiceAccountParams = {
    serviceAccountId: string;
    name?: string;
    description?: string;
};

export const useUpdateServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ serviceAccountId, name, description }: UpdateServiceAccountParams) => {
            const request = new UpdateServiceAccountRequest({
                serviceAccountId,
                name,
                description,
            });
            const response = await api.serviceAccountService.updateServiceAccount(request);

            if (!response?.serviceAccount) {
                throw new Error("Failed to update service account");
            }

            return toPlainServiceAccount(response.serviceAccount);
        },
        onSuccess: (updatedServiceAccount) => {
            setServiceAccountInCache(queryClient, updatedServiceAccount.id, updatedServiceAccount);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (serviceAccountId: string) => {
            const request = new DeleteServiceAccountRequest({ serviceAccountId });
            await api.serviceAccountService.deleteServiceAccount(request);
            return serviceAccountId;
        },
        onSuccess: (serviceAccountId) => {
            setServiceAccountInCache(queryClient, serviceAccountId, undefined);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UseCreateServiceAccountAccessTokenParams = {
    serviceAccountId: string;
    enabled?: boolean;
};

export const useCreateServiceAccountAccessToken = ({
    serviceAccountId,
    enabled = true,
}: UseCreateServiceAccountAccessTokenParams) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 30;
    return useQuery<string, Error>({
        queryKey: serviceAccountsQueryKey.accessToken(serviceAccountId),
        queryFn: async (): Promise<string> => {
            const { token } = await api.serviceAccountService.createServiceAccountAccessToken({
                serviceAccountId,
            });
            if (!token) {
                throw new Error("No access token provided");
            }

            return token;
        },
        enabled: !!serviceAccountId && enabled,
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
    });
};
